import supabase from '../supabase/supabase';

const TABLES = {
  workspaces: 'workspaces',
  sections: 'sections',
  applications: 'applications',
  quickLaunch: 'quick_launch_items'
};

const FUNCTIONS = {
  defaultWorkspace: 'tefolio_default_workspace',
  pin: 'tefolio_pin_application',
  createQuickApp: 'tefolio_create_quick_application',
  reorderSections: 'tefolio_reorder_sections',
  moveApplication: 'tefolio_move_application',
  moveToVault: 'tefolio_move_application_to_vault',
  restoreFromVault: 'tefolio_restore_vault_application',
  reorderQuickLaunch: 'tefolio_reorder_quick_launch',
  deleteSection: 'tefolio_delete_section',
  importLegacy: 'tefolio_import_legacy_workspace'
};

function resultOf(result) {
  if (result.error) throw result.error;
  return result.data;
}

function requireAffected(rows, message) {
  if (!rows?.length) throw new Error(message);
  return rows;
}

// The workspace row never changes after a write, so refreshes pass the one already
// loaded and skip the extra round trip that resolves it.
export async function loadWorkspace(userId, knownWorkspace) {
  let workspace = knownWorkspace && knownWorkspace.owner_id === userId ? knownWorkspace : null;
  if (!workspace) {
    const rows = resultOf(await supabase.rpc(FUNCTIONS.defaultWorkspace));
    workspace = rows?.[0];
  }
  if (!workspace || workspace.owner_id !== userId) throw new Error('Unable to load workspace.');

  const [sectionResult, applicationResult, quickResult] = await Promise.all([
    supabase.from(TABLES.sections).select('*').eq('workspace_id', workspace.id).order('display_order').order('id'),
    supabase.from(TABLES.applications).select('*').eq('workspace_id', workspace.id).order('display_order').order('id'),
    supabase.from(TABLES.quickLaunch).select('*').eq('workspace_id', workspace.id).order('display_order').order('id')
  ]);

  const sections = resultOf(sectionResult).map((row) => ({
    id: row.id,
    order: row.display_order,
    title: row.name,
    apps: []
  }));
  const storedApps = resultOf(applicationResult).map((row) => ({
    id: row.id,
    name: row.name,
    url: row.url,
    faviconUrl: row.favicon_url,
    note: row.note || '',
    sectionId: row.section_id,
    order: row.display_order,
    isVaulted: row.is_vaulted
  }));
  const allApps = storedApps.filter((app) => !app.isVaulted);
  const vaultApps = storedApps.filter((app) => app.isVaulted);
  const sectionMap = new Map(sections.map((section) => [section.id, section]));
  allApps.forEach((app) => sectionMap.get(app.sectionId)?.apps.push(app));
  sections.forEach((section) => {
    section.apps.sort((a, b) => a.order - b.order || a.id.localeCompare(b.id));
  });

  const activeIds = new Set(allApps.map((app) => app.id));
  const quickLaunchIds = resultOf(quickResult)
    .map((row) => row.application_id)
    .filter((id) => activeIds.has(id));

  return { workspace, sections, allApps, vaultApps, quickLaunchIds };
}

function nextOrder(rows) {
  return rows.reduce((highest, row) => Math.max(highest, row.display_order ?? -1), -1) + 1;
}

export async function createSection(workspaceId, ownerId, title) {
  const rows = resultOf(await supabase.from(TABLES.sections).select('display_order')
    .eq('workspace_id', workspaceId).order('display_order'));
  resultOf(await supabase.from(TABLES.sections).insert({
    workspace_id: workspaceId, owner_id: ownerId, name: title, display_order: nextOrder(rows)
  }));
}

export async function renameSection(workspaceId, id, title) {
  requireAffected(resultOf(await supabase.from(TABLES.sections).update({ name: title })
    .eq('id', id).eq('workspace_id', workspaceId).select('id')),
  'This group no longer exists.');
}

export async function deleteSection(workspaceId, id) {
  resultOf(await supabase.rpc(FUNCTIONS.deleteSection, {
    p_workspace_id: workspaceId, p_section_id: id
  }));
}

export async function reorderSection(workspaceId, activeId, overId) {
  resultOf(await supabase.rpc(FUNCTIONS.reorderSections, {
    p_workspace_id: workspaceId, p_active_id: activeId, p_over_id: overId
  }));
}

export async function createApplication(workspaceId, ownerId, sectionId, values) {
  const rows = resultOf(await supabase.from(TABLES.applications).select('display_order')
    .eq('workspace_id', workspaceId).eq('section_id', sectionId).order('display_order'));
  resultOf(await supabase.from(TABLES.applications).insert({
    workspace_id: workspaceId,
    owner_id: ownerId,
    section_id: sectionId,
    name: values.name,
    url: values.url,
    display_order: nextOrder(rows)
  }));
}

export async function createVaultTool(workspaceId, ownerId, values) {
  resultOf(await supabase.from(TABLES.applications).insert({
    workspace_id: workspaceId,
    owner_id: ownerId,
    section_id: null,
    name: values.name,
    url: values.url,
    note: values.note || null,
    is_vaulted: true,
    display_order: 0
  }));
}

export async function updateVaultTool(workspaceId, id, values) {
  requireAffected(resultOf(await supabase.from(TABLES.applications)
    .update({ name: values.name, url: values.url, note: values.note || null })
    .eq('id', id).eq('workspace_id', workspaceId).eq('is_vaulted', true).select('id')),
  'This Vault tool no longer exists.');
}

export async function createQuickLaunchApplication(workspaceId, name, url) {
  return resultOf(await supabase.rpc(FUNCTIONS.createQuickApp, {
    p_workspace_id: workspaceId, p_name: name, p_url: url
  }));
}

export async function pinApplication(workspaceId, applicationId) {
  resultOf(await supabase.rpc(FUNCTIONS.pin, {
    p_workspace_id: workspaceId, p_application_id: applicationId
  }));
}

export async function unpinApplication(workspaceId, applicationId) {
  requireAffected(resultOf(await supabase.from(TABLES.quickLaunch).delete()
    .eq('workspace_id', workspaceId).eq('application_id', applicationId).select('id')),
  'This Quick Launch pin no longer exists.');
}

export async function reorderQuickLaunch(workspaceId, activeId, overId) {
  resultOf(await supabase.rpc(FUNCTIONS.reorderQuickLaunch, {
    p_workspace_id: workspaceId,
    p_active_application_id: activeId,
    p_over_application_id: overId
  }));
}

export async function updateApplication(workspaceId, id, values, sectionId) {
  const changes = { name: values.name, url: values.url };
  if (sectionId !== undefined) {
    changes.section_id = sectionId;
    if (sectionId) {
      const rows = resultOf(await supabase.from(TABLES.applications).select('display_order')
        .eq('workspace_id', workspaceId).eq('section_id', sectionId).neq('id', id));
      changes.display_order = nextOrder(rows);
    } else {
      changes.display_order = 0;
    }
  }
  requireAffected(resultOf(await supabase.from(TABLES.applications).update(changes)
    .eq('id', id).eq('workspace_id', workspaceId).select('id')),
  'This application no longer exists.');
}

export async function deleteApplication(workspaceId, id) {
  requireAffected(resultOf(await supabase.from(TABLES.applications).delete()
    .eq('id', id).eq('workspace_id', workspaceId).select('id')),
  'This application no longer exists.');
}

export async function moveApplication(workspaceId, id, sectionId, position) {
  const safePosition = Number.isFinite(position) ? Math.max(0, Math.trunc(position)) : 2147483647;
  resultOf(await supabase.rpc(FUNCTIONS.moveApplication, {
    p_workspace_id: workspaceId,
    p_application_id: id,
    p_target_section_id: sectionId,
    p_position: safePosition
  }));
}

export async function moveApplicationToVault(workspaceId, id) {
  resultOf(await supabase.rpc(FUNCTIONS.moveToVault, {
    p_workspace_id: workspaceId,
    p_application_id: id
  }));
}

export async function restoreVaultApplication(workspaceId, id, sectionId) {
  resultOf(await supabase.rpc(FUNCTIONS.restoreFromVault, {
    p_workspace_id: workspaceId,
    p_application_id: id,
    p_target_section_id: sectionId
  }));
}

export async function deleteVaultTool(workspaceId, id) {
  requireAffected(resultOf(await supabase.from(TABLES.applications).delete()
    .eq('id', id).eq('workspace_id', workspaceId).eq('is_vaulted', true).select('id')),
  'This Vault tool no longer exists.');
}

export async function importLegacyWorkspace(workspaceId, snapshot) {
  return resultOf(await supabase.rpc(FUNCTIONS.importLegacy, {
    p_workspace_id: workspaceId,
    p_sections: snapshot.sections,
    p_applications: snapshot.applications,
    p_quick_launch: snapshot.quickLaunch
  }));
}